import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Translation interfaces
interface CustomerTranslations {
  // Header
  headerTitle: string;
  headerSubtitle: string;
  
  // Customer Info Form
  formTitle: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  companyLabel: string;
  companyPlaceholder: string;
  countryLabel: string;
  countryPlaceholder: string;
  categoryLabel: string;
  categoryPlaceholder: string;
  languageLabel: string;
  languagePlaceholder: string;
  
  // Info Messages
  savedInfoMessage: string;
  clearSavedInfo: string;
  invalidEmail: string;
  
  // Ticket Messages
  ticketCreated: string;
  conversationStarted: string;
  closeTicket: string;
  close: string;
  
  // Chat Messages
  startConversation: string;
  fillDetailsFirst: string;
  typeMessage: string;
  fillDetailsAbove: string;
  you: string;
  supportAgent: string;
  initialMessage: string;
  
  // File Upload
  attachFiles: string;
  takePhoto: string;
  uploading: string;
  download: string;
  
  // Typing Indicator
  agentTyping: string;
  
  // Ticket Status
  conversationClosed: string;
  conversationClosedInactivity: string;
  ticketClosedByAgent: string;
  conversationEnded: string;
  sessionTimedOut: string;
  ticketResolvedByAgent: string;
  thankYou: string;
  thankYouResolved: string;
  startNewConversation: string;
  
  // Close Confirmation Dialog
  closeConversationTitle: string;
  closeConversationMessage: string;
  cancel: string;
  closeConversationButton: string;
  
  // Resolution Feedback Dialog
  problemResolvedTitle: string;
  problemResolvedMessage: string;
  problemResolved: string;
  problemPartiallyResolved: string;
  problemNotResolved: string;
  
  // Abandonment Warning Dialog
  stillThereTitle: string;
  stillThereMessage: string;
  stillThereCountdown: string;
  yesImHere: string;
  closeChat: string;
  
  // Persistent Survey
  surveyTitle: string;
  surveyTitleCompleted: string;
  surveyMessage: string;
  surveyResolved: string;
  surveyPartiallyResolved: string;
  surveyNotResolved: string;
  
  // System Messages
  systemMessage: string;
  conversationClosedDueToInactivity: string;
  
  // Error Messages
  failedToStart: string;
  failedToSend: string;
  failedToUpload: string;
  failedToClose: string;
  failedToSubmitFeedback: string;
  cameraPermissionError: string;
  
  // File Sizes
  bytes: string;
  kb: string;
  mb: string;
  gb: string;
  
  // Dashboard - Welcome & Auth
  welcomeBack: string;
  logout: string;
  
  // Dashboard - Sidebar Banners
  accessYourHistory: string;
  accessYourHistoryDesc: string;
  viewTicketHistoryDesc: string;
  viewTicketHistoryLink: string;
  login: string;
  
  // Dashboard - Survey Banner
  helpUsImprove: string;
  helpUsImproveDesc: string;
  takeSurvey: string;
  
  // Dashboard - Quick Tips
  quickTips: string;
  quickTip1: string;
  quickTip2: string;
  quickTip3: string;
  
  // Dashboard - Tab Navigation
  supportChat: string;
  history: string;
  profile: string;
  
  // Welcome Screen
  welcomeToSupport: string;
  welcomeToSupportDesc: string;
  startChat: string;
}

// Available languages in the system
const AVAILABLE_LANGUAGES = ['en', 'es', 'pt', 'de', 'fr'];

// Function to detect browser language
const detectBrowserLanguage = (): string => {
  try {
    // Get browser language preference
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    console.log('🌍 Browser language detected:', browserLang);
    
    // Extract language code (e.g., 'en-US' -> 'en')
    const langCode = browserLang.toLowerCase().split('-')[0];
    
    // Check if the language is supported
    if (AVAILABLE_LANGUAGES.includes(langCode)) {
      console.log('✅ Language supported:', langCode);
      return langCode;
    }
    
    // Check for language variants/mappings
    const languageMappings: Record<string, string> = {
      'ca': 'es', // Catalan -> Spanish
      'gl': 'es', // Galician -> Spanish
      'eu': 'es', // Basque -> Spanish
      'it': 'en', // Italian -> English (closest)
      'nl': 'en', // Dutch -> English
      'sv': 'en', // Swedish -> English
      'da': 'en', // Danish -> English
      'no': 'en', // Norwegian -> English
      'fi': 'en', // Finnish -> English
      'pl': 'en', // Polish -> English
      'ru': 'en', // Russian -> English
      'ja': 'en', // Japanese -> English
      'ko': 'en', // Korean -> English
      'zh': 'en', // Chinese -> English
      'ar': 'en', // Arabic -> English
    };
    
    if (languageMappings[langCode]) {
      console.log('📍 Language mapped:', langCode, '->', languageMappings[langCode]);
      return languageMappings[langCode];
    }
    
    // Default to English if not supported
    console.log('🔄 Language not supported, defaulting to English:', langCode);
    return 'en';
  } catch (error) {
    console.error('❌ Error detecting browser language:', error);
    return 'en';
  }
};

const translations: Record<string, CustomerTranslations> = {
  en: {
    // Header
    headerTitle: 'NeuroVirtual Support',
    headerSubtitle: 'Get help from our support team',
    
    // Customer Info Form
    formTitle: "Let's get started",
    nameLabel: 'Your Name *',
    namePlaceholder: 'John Doe',
    emailLabel: 'Email Address *',
    emailPlaceholder: 'john@company.com',
    phoneLabel: 'Phone Number',
    phonePlaceholder: '+1-555-0123',
    companyLabel: 'Company',
    companyPlaceholder: 'Company Inc.',
    countryLabel: 'Country *',
    countryPlaceholder: 'Select your country...',
    categoryLabel: 'What can we help you with? *',
    categoryPlaceholder: 'Select a topic...',
    languageLabel: 'Preferred Language *',
    languagePlaceholder: 'Select your language...',
    
    // Info Messages
    savedInfoMessage: 'Your information is saved for future visits',
    clearSavedInfo: 'Clear Saved Info',
    invalidEmail: 'Please enter a valid email address',
    
    // Ticket Messages
    ticketCreated: 'Ticket Created',
    conversationStarted: 'Your conversation has started. Our support team will respond shortly.',
    closeTicket: 'Close Ticket',
    close: 'Close',
    
    // Chat Messages
    startConversation: 'Start a conversation with our support team!',
    fillDetailsFirst: 'Fill in your details above and type your message below.',
    typeMessage: 'Type your message...',
    fillDetailsAbove: 'Please fill in your details above first',
    you: 'You',
    supportAgent: 'Support Agent',
    initialMessage: 'Hello, I need assistance with my NeuroVirtual device.',
    
    // File Upload
    attachFiles: 'Attach files',
    takePhoto: 'Take photo',
    uploading: 'Uploading',
    download: 'Download',
    
    // Typing Indicator
    agentTyping: 'Agent is typing...',
    
    // Ticket Status
    conversationClosed: 'Conversation Closed',
    conversationClosedInactivity: 'Conversation Closed Due to Inactivity',
    ticketClosedByAgent: 'Ticket Closed by Agent',
    conversationEnded: 'Conversation Ended',
    sessionTimedOut: 'Session Timed Out',
    ticketResolvedByAgent: 'Ticket Resolved by Agent',
    thankYou: 'Thank you for contacting our support team.',
    thankYouResolved: 'Your support request has been resolved. Thank you for contacting our support team.',
    startNewConversation: 'Start New Conversation',
    
    // Close Confirmation Dialog
    closeConversationTitle: 'Close Conversation',
    closeConversationMessage: 'Are you sure you want to close this conversation? This action cannot be undone. If you need further assistance later, you\'ll need to start a new conversation.',
    cancel: 'Cancel',
    closeConversationButton: 'Close Conversation',
    
    // Resolution Feedback Dialog
    problemResolvedTitle: 'Was your problem resolved?',
    problemResolvedMessage: 'Before we close this conversation, please let us know if your issue was resolved. This helps us improve our support.',
    problemResolved: 'Yes, my problem was resolved',
    problemPartiallyResolved: 'Partially resolved',
    problemNotResolved: 'No, my problem was not resolved',
    
    // Abandonment Warning Dialog
    stillThereTitle: 'Are you still there?',
    stillThereMessage: 'We haven\'t heard from you in a while. To keep this conversation active and ensure you receive assistance, please let us know you\'re still here.',
    stillThereCountdown: 'This chat will be automatically closed in {seconds} seconds if we don\'t hear from you.',
    yesImHere: 'Yes, I\'m here',
    closeChat: 'Close chat',
    
    // Persistent Survey
    surveyTitle: 'How are we doing?',
    surveyTitleCompleted: 'Thank you for your feedback!',
    surveyMessage: 'Please let us know if your problem has been resolved',
    surveyResolved: 'Yes, resolved',
    surveyPartiallyResolved: 'Partially resolved',
    surveyNotResolved: 'Not resolved',
    
    // System Messages
    systemMessage: 'System',
    conversationClosedDueToInactivity: 'Conversation Closed Due to Inactivity',
    
    // Error Messages
    failedToStart: 'Failed to start conversation. Please try again.',
    failedToSend: 'Failed to send message. Please try again.',
    failedToUpload: 'Failed to upload files',
    failedToClose: 'Failed to close ticket. Please try again.',
    failedToSubmitFeedback: 'Failed to submit feedback',
    cameraPermissionError: 'Unable to access camera. Please check permissions or try uploading a file instead.',
    
    // File Sizes
    bytes: 'Bytes',
    kb: 'KB',
    mb: 'MB',
    gb: 'GB',
    
    // Dashboard - Welcome & Auth
    welcomeBack: 'Welcome',
    logout: 'Logout',
    
    // Dashboard - Sidebar Banners
    accessYourHistory: 'Access Your History',
    accessYourHistoryDesc: 'View your ticket history and manage your profile',
    viewTicketHistoryDesc: 'View past tickets and track your support requests',
    viewTicketHistoryLink: 'View Ticket History →',
    login: 'Login',
    
    // Dashboard - Survey Banner
    helpUsImprove: 'Help Us Improve',
    helpUsImproveDesc: 'Share your experience and help us serve you better',
    takeSurvey: 'Take Survey',
    
    // Dashboard - Quick Tips
    quickTips: 'Quick Tips',
    quickTip1: 'Be specific about your issue for faster resolution',
    quickTip2: 'Include error messages or screenshots when possible',
    quickTip3: 'Our AI agent is here 24/7 to help you',
    
    // Dashboard - Tab Navigation
    supportChat: 'Support Chat',
    history: 'History',
    profile: 'Profile',
    
    // Welcome Screen
    welcomeToSupport: 'Welcome to NeuroVirtual Support',
    welcomeToSupportDesc: 'We\'re here to help you with any questions or technical issues.',
    startChat: 'Start Chat'
  },
  
  es: {
    // Header
    headerTitle: 'Soporte NeuroVirtual',
    headerSubtitle: 'Obtén ayuda de nuestro equipo de soporte',
    
    // Customer Info Form
    formTitle: 'Comencemos',
    nameLabel: 'Tu Nombre *',
    namePlaceholder: 'Juan Pérez',
    emailLabel: 'Dirección de Email *',
    emailPlaceholder: 'juan@empresa.com',
    phoneLabel: 'Número de Teléfono',
    phonePlaceholder: '+34-555-0123',
    companyLabel: 'Empresa',
    companyPlaceholder: 'Empresa S.A.',
    countryLabel: 'País *',
    countryPlaceholder: 'Selecciona tu país...',
    categoryLabel: '¿En qué podemos ayudarte? *',
    categoryPlaceholder: 'Selecciona un tema...',
    languageLabel: 'Idioma Preferido *',
    languagePlaceholder: 'Selecciona tu idioma...',
    
    // Info Messages
    savedInfoMessage: 'Tu información se guarda para futuras visitas',
    clearSavedInfo: 'Borrar Información Guardada',
    invalidEmail: 'Por favor ingresa una dirección de email válida',
    
    // Ticket Messages
    ticketCreated: 'Ticket Creado',
    conversationStarted: 'Tu conversación ha comenzado. Nuestro equipo de soporte responderá en breve.',
    closeTicket: 'Cerrar Ticket',
    close: 'Cerrar',
    
    // Chat Messages
    startConversation: '¡Inicia una conversación con nuestro equipo de soporte!',
    fillDetailsFirst: 'Completa tus datos arriba y escribe tu mensaje abajo.',
    typeMessage: 'Escribe tu mensaje...',
    fillDetailsAbove: 'Por favor completa tus datos arriba primero',
    you: 'Tú',
    supportAgent: 'Agente de Soporte',
    initialMessage: 'Hola, necesito asistencia con mi dispositivo NeuroVirtual.',
    
    // File Upload
    attachFiles: 'Adjuntar archivos',
    takePhoto: 'Tomar foto',
    uploading: 'Subiendo',
    download: 'Descargar',
    
    // Typing Indicator
    agentTyping: 'El agente está escribiendo...',
    
    // Ticket Status
    conversationClosed: 'Conversación Cerrada',
    conversationClosedInactivity: 'Conversación Cerrada por Inactividad',
    ticketClosedByAgent: 'Ticket Cerrado por Agente',
    conversationEnded: 'Conversación Terminada',
    sessionTimedOut: 'Sesión Expirada',
    ticketResolvedByAgent: 'Ticket Resuelto por Agente',
    thankYou: 'Gracias por contactar a nuestro equipo de soporte.',
    thankYouResolved: 'Tu solicitud de soporte ha sido resuelta. Gracias por contactar a nuestro equipo de soporte.',
    startNewConversation: 'Iniciar Nueva Conversación',
    
    // Close Confirmation Dialog
    closeConversationTitle: 'Cerrar Conversación',
    closeConversationMessage: '¿Estás seguro de que quieres cerrar esta conversación? Esta acción no se puede deshacer. Si necesitas ayuda más tarde, tendrás que iniciar una nueva conversación.',
    cancel: 'Cancelar',
    closeConversationButton: 'Cerrar Conversación',
    
    // Resolution Feedback Dialog
    problemResolvedTitle: '¿Se resolvió tu problema?',
    problemResolvedMessage: 'Antes de cerrar esta conversación, dinos si tu problema fue resuelto. Esto nos ayuda a mejorar nuestro soporte.',
    problemResolved: 'Sí, mi problema fue resuelto',
    problemPartiallyResolved: 'Parcialmente resuelto',
    problemNotResolved: 'No, mi problema no fue resuelto',
    
    // Abandonment Warning Dialog
    stillThereTitle: '¿Sigues ahí?',
    stillThereMessage: 'No hemos sabido de ti en un tiempo. Para mantener esta conversación activa y asegurar que recibas asistencia, dinos que sigues aquí.',
    stillThereCountdown: 'Este chat se cerrará automáticamente en {seconds} segundos si no sabemos de ti.',
    yesImHere: 'Sí, estoy aquí',
    closeChat: 'Cerrar chat',
    
    // Persistent Survey
    surveyTitle: '¿Cómo lo estamos haciendo?',
    surveyTitleCompleted: '¡Gracias por tu comentario!',
    surveyMessage: 'Dinos si tu problema ha sido resuelto',
    surveyResolved: 'Sí, resuelto',
    surveyPartiallyResolved: 'Parcialmente resuelto',
    surveyNotResolved: 'No resuelto',
    
    // System Messages
    systemMessage: 'Sistema',
    conversationClosedDueToInactivity: 'Conversación Cerrada por Inactividad',
    
    // Error Messages
    failedToStart: 'Error al iniciar conversación. Por favor intenta de nuevo.',
    failedToSend: 'Error al enviar mensaje. Por favor intenta de nuevo.',
    failedToUpload: 'Error al subir archivos',
    failedToClose: 'Error al cerrar ticket. Por favor intenta de nuevo.',
    failedToSubmitFeedback: 'Error al enviar comentario',
    cameraPermissionError: 'No se puede acceder a la cámara. Verifica los permisos o intenta subir un archivo.',
    
    // File Sizes
    bytes: 'Bytes',
    kb: 'KB',
    mb: 'MB',
    gb: 'GB',
    
    // Dashboard - Welcome & Auth
    welcomeBack: 'Bienvenido',
    logout: 'Cerrar sesión',
    
    // Dashboard - Sidebar Banners
    accessYourHistory: 'Accede a tu Historial',
    accessYourHistoryDesc: 'Ve tu historial de tickets y gestiona tu perfil',
    viewTicketHistoryDesc: 'Ve tickets anteriores y sigue tus solicitudes de soporte',
    viewTicketHistoryLink: 'Ver Historial de Tickets →',
    login: 'Iniciar sesión',
    
    // Dashboard - Survey Banner
    helpUsImprove: 'Ayúdanos a Mejorar',
    helpUsImproveDesc: 'Comparte tu experiencia y ayúdanos a servirte mejor',
    takeSurvey: 'Tomar Encuesta',
    
    // Dashboard - Quick Tips
    quickTips: 'Consejos Rápidos',
    quickTip1: 'Sé específico sobre tu problema para una resolución más rápida',
    quickTip2: 'Incluye mensajes de error o capturas de pantalla cuando sea posible',
    quickTip3: 'Nuestro agente IA está aquí 24/7 para ayudarte',
    
    // Dashboard - Tab Navigation
    supportChat: 'Chat de Soporte',
    history: 'Historial',
    profile: 'Perfil',
    
    // Welcome Screen
    welcomeToSupport: 'Bienvenido al Soporte de NeuroVirtual',
    welcomeToSupportDesc: 'Estamos aquí para ayudarte con cualquier pregunta o problema técnico.',
    startChat: 'Iniciar Chat'
  },
  
  pt: {
    // Header
    headerTitle: 'Suporte NeuroVirtual',
    headerSubtitle: 'Obtenha ajuda da nossa equipe de suporte',
    
    // Customer Info Form
    formTitle: 'Vamos começar',
    nameLabel: 'Seu Nome *',
    namePlaceholder: 'João Silva',
    emailLabel: 'Endereço de Email *',
    emailPlaceholder: 'joao@empresa.com',
    phoneLabel: 'Número de Telefone',
    phonePlaceholder: '+55-11-5555-0123',
    companyLabel: 'Empresa',
    companyPlaceholder: 'Empresa Ltda.',
    countryLabel: 'País *',
    countryPlaceholder: 'Selecione seu país...',
    categoryLabel: 'Como podemos ajudá-lo? *',
    categoryPlaceholder: 'Selecione um tópico...',
    languageLabel: 'Idioma Preferido *',
    languagePlaceholder: 'Selecione seu idioma...',
    
    // Info Messages
    savedInfoMessage: 'Suas informações são salvas para visitas futuras',
    clearSavedInfo: 'Limpar Informações Salvas',
    invalidEmail: 'Por favor digite um endereço de email válido',
    
    // Ticket Messages
    ticketCreated: 'Ticket Criado',
    conversationStarted: 'Sua conversa começou. Nossa equipe de suporte responderá em breve.',
    closeTicket: 'Fechar Ticket',
    close: 'Fechar',
    
    // Chat Messages
    startConversation: 'Inicie uma conversa com nossa equipe de suporte!',
    fillDetailsFirst: 'Preencha seus dados acima e digite sua mensagem abaixo.',
    typeMessage: 'Digite sua mensagem...',
    fillDetailsAbove: 'Por favor preencha seus dados acima primeiro',
    you: 'Você',
    supportAgent: 'Agente de Suporte',
    initialMessage: 'Olá, preciso de assistência com meu dispositivo NeuroVirtual.',
    
    // File Upload
    attachFiles: 'Anexar arquivos',
    takePhoto: 'Tirar foto',
    uploading: 'Enviando',
    download: 'Baixar',
    
    // Typing Indicator
    agentTyping: 'Agente está digitando...',
    
    // Ticket Status
    conversationClosed: 'Conversa Fechada',
    conversationClosedInactivity: 'Conversa Fechada por Inatividade',
    ticketClosedByAgent: 'Ticket Fechado pelo Agente',
    conversationEnded: 'Conversa Encerrada',
    sessionTimedOut: 'Sessão Expirada',
    ticketResolvedByAgent: 'Ticket Resolvido pelo Agente',
    thankYou: 'Obrigado por entrar em contato com nossa equipe de suporte.',
    thankYouResolved: 'Sua solicitação de suporte foi resolvida. Obrigado por entrar em contato com nossa equipe de suporte.',
    startNewConversation: 'Iniciar Nova Conversa',
    
    // Close Confirmation Dialog
    closeConversationTitle: 'Fechar Conversa',
    closeConversationMessage: 'Tem certeza de que deseja fechar esta conversa? Esta ação não pode ser desfeita. Se precisar de ajuda mais tarde, será necessário iniciar uma nova conversa.',
    cancel: 'Cancelar',
    closeConversationButton: 'Fechar Conversa',
    
    // Resolution Feedback Dialog
    problemResolvedTitle: 'Seu problema foi resolvido?',
    problemResolvedMessage: 'Antes de fecharmos esta conversa, nos informe se seu problema foi resolvido. Isso nos ajuda a melhorar nosso suporte.',
    problemResolved: 'Sim, meu problema foi resolvido',
    problemPartiallyResolved: 'Parcialmente resolvido',
    problemNotResolved: 'Não, meu problema não foi resolvido',
    
    // Abandonment Warning Dialog
    stillThereTitle: 'Você ainda está aí?',
    stillThereMessage: 'Não tivemos notícias suas há um tempo. Para manter esta conversa ativa e garantir que você receba assistência, nos informe que ainda está aqui.',
    stillThereCountdown: 'Este chat será fechado automaticamente em {seconds} segundos se não soubermos de você.',
    yesImHere: 'Sim, estou aqui',
    closeChat: 'Fechar chat',
    
    // Persistent Survey
    surveyTitle: 'Como estamos indo?',
    surveyTitleCompleted: 'Obrigado pelo seu feedback!',
    surveyMessage: 'Por favor nos informe se seu problema foi resolvido',
    surveyResolved: 'Sim, resolvido',
    surveyPartiallyResolved: 'Parcialmente resolvido',
    surveyNotResolved: 'Não resolvido',
    
    // System Messages
    systemMessage: 'Sistema',
    conversationClosedDueToInactivity: 'Conversa Fechada por Inatividade',
    
    // Error Messages
    failedToStart: 'Falha ao iniciar conversa. Por favor tente novamente.',
    failedToSend: 'Falha ao enviar mensagem. Por favor tente novamente.',
    failedToUpload: 'Falha ao enviar arquivos',
    failedToClose: 'Falha ao fechar ticket. Por favor tente novamente.',
    failedToSubmitFeedback: 'Falha ao enviar feedback',
    cameraPermissionError: 'Não é possível acessar a câmera. Verifique as permissões ou tente enviar um arquivo.',
    
    // File Sizes
    bytes: 'Bytes',
    kb: 'KB',
    mb: 'MB',
    gb: 'GB',
    
    // Dashboard - Welcome & Auth
    welcomeBack: 'Bem-vindo',
    logout: 'Sair',
    
    // Dashboard - Sidebar Banners
    accessYourHistory: 'Acesse seu Histórico',
    accessYourHistoryDesc: 'Veja seu histórico de tickets e gerencie seu perfil',
    viewTicketHistoryDesc: 'Veja tickets anteriores e acompanhe suas solicitações de suporte',
    viewTicketHistoryLink: 'Ver Histórico de Tickets →',
    login: 'Entrar',
    
    // Dashboard - Survey Banner
    helpUsImprove: 'Ajude-nos a Melhorar',
    helpUsImproveDesc: 'Compartilhe sua experiência e nos ajude a servi-lo melhor',
    takeSurvey: 'Fazer Pesquisa',
    
    // Dashboard - Quick Tips
    quickTips: 'Dicas Rápidas',
    quickTip1: 'Seja específico sobre seu problema para uma resolução mais rápida',
    quickTip2: 'Inclua mensagens de erro ou capturas de tela quando possível',
    quickTip3: 'Nosso agente IA está aqui 24/7 para ajudá-lo',
    
    // Dashboard - Tab Navigation
    supportChat: 'Chat de Suporte',
    history: 'Histórico',
    profile: 'Perfil',
    
    // Welcome Screen
    welcomeToSupport: 'Bem-vindo ao Suporte NeuroVirtual',
    welcomeToSupportDesc: 'Estamos aqui para ajudá-lo com qualquer dúvida ou problema técnico.',
    startChat: 'Iniciar Chat'
  },
  
  de: {
    // Header
    headerTitle: 'NeuroVirtual Support',
    headerSubtitle: 'Erhalten Sie Hilfe von unserem Support-Team',
    
    // Customer Info Form
    formTitle: 'Lass uns anfangen',
    nameLabel: 'Ihr Name *',
    namePlaceholder: 'Max Mustermann',
    emailLabel: 'E-Mail-Adresse *',
    emailPlaceholder: 'max@unternehmen.de',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '+49-555-0123',
    companyLabel: 'Unternehmen',
    companyPlaceholder: 'Unternehmen GmbH',
    countryLabel: 'Land *',
    countryPlaceholder: 'Wählen Sie Ihr Land...',
    categoryLabel: 'Womit können wir Ihnen helfen? *',
    categoryPlaceholder: 'Wählen Sie ein Thema...',
    languageLabel: 'Bevorzugte Sprache *',
    languagePlaceholder: 'Wählen Sie Ihre Sprache...',
    
    // Info Messages
    savedInfoMessage: 'Ihre Informationen werden für zukünftige Besuche gespeichert',
    clearSavedInfo: 'Gespeicherte Informationen löschen',
    invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    
    // Ticket Messages
    ticketCreated: 'Ticket erstellt',
    conversationStarted: 'Ihr Gespräch hat begonnen. Unser Support-Team wird in Kürze antworten.',
    closeTicket: 'Ticket schließen',
    close: 'Schließen',
    
    // Chat Messages
    startConversation: 'Beginnen Sie ein Gespräch mit unserem Support-Team!',
    fillDetailsFirst: 'Füllen Sie Ihre Daten oben aus und geben Sie Ihre Nachricht unten ein.',
    typeMessage: 'Geben Sie Ihre Nachricht ein...',
    fillDetailsAbove: 'Bitte füllen Sie zuerst Ihre Daten oben aus',
    you: 'Sie',
    supportAgent: 'Support-Agent',
    initialMessage: 'Hallo, ich benötige Hilfe mit meinem NeuroVirtual-Gerät.',
    
    // File Upload
    attachFiles: 'Dateien anhängen',
    takePhoto: 'Foto aufnehmen',
    uploading: 'Hochladen',
    download: 'Herunterladen',
    
    // Typing Indicator
    agentTyping: 'Agent tippt...',
    
    // Ticket Status
    conversationClosed: 'Gespräch geschlossen',
    conversationClosedInactivity: 'Gespräch wegen Inaktivität geschlossen',
    ticketClosedByAgent: 'Ticket vom Agent geschlossen',
    conversationEnded: 'Gespräch beendet',
    sessionTimedOut: 'Sitzung abgelaufen',
    ticketResolvedByAgent: 'Ticket vom Agent gelöst',
    thankYou: 'Vielen Dank, dass Sie unser Support-Team kontaktiert haben.',
    thankYouResolved: 'Ihre Support-Anfrage wurde gelöst. Vielen Dank, dass Sie unser Support-Team kontaktiert haben.',
    startNewConversation: 'Neues Gespräch beginnen',
    
    // Close Confirmation Dialog
    closeConversationTitle: 'Gespräch schließen',
    closeConversationMessage: 'Sind Sie sicher, dass Sie dieses Gespräch schließen möchten? Diese Aktion kann nicht rückgängig gemacht werden. Wenn Sie später Hilfe benötigen, müssen Sie ein neues Gespräch beginnen.',
    cancel: 'Abbrechen',
    closeConversationButton: 'Gespräch schließen',
    
    // Resolution Feedback Dialog
    problemResolvedTitle: 'Wurde Ihr Problem gelöst?',
    problemResolvedMessage: 'Bevor wir dieses Gespräch schließen, teilen Sie uns bitte mit, ob Ihr Problem gelöst wurde. Dies hilft uns, unseren Support zu verbessern.',
    problemResolved: 'Ja, mein Problem wurde gelöst',
    problemPartiallyResolved: 'Teilweise gelöst',
    problemNotResolved: 'Nein, mein Problem wurde nicht gelöst',
    
    // Abandonment Warning Dialog
    stillThereTitle: 'Sind Sie noch da?',
    stillThereMessage: 'Wir haben eine Weile nichts von Ihnen gehört. Um dieses Gespräch aktiv zu halten und sicherzustellen, dass Sie Hilfe erhalten, lassen Sie uns wissen, dass Sie noch da sind.',
    stillThereCountdown: 'Dieser Chat wird automatisch in {seconds} Sekunden geschlossen, wenn wir nichts von Ihnen hören.',
    yesImHere: 'Ja, ich bin hier',
    closeChat: 'Chat schließen',
    
    // Persistent Survey
    surveyTitle: 'Wie machen wir das?',
    surveyTitleCompleted: 'Vielen Dank für Ihr Feedback!',
    surveyMessage: 'Bitte teilen Sie uns mit, ob Ihr Problem gelöst wurde',
    surveyResolved: 'Ja, gelöst',
    surveyPartiallyResolved: 'Teilweise gelöst',
    surveyNotResolved: 'Nicht gelöst',
    
    // System Messages
    systemMessage: 'System',
    conversationClosedDueToInactivity: 'Gespräch wegen Inaktivität geschlossen',
    
    // Error Messages
    failedToStart: 'Gespräch konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    failedToSend: 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    failedToUpload: 'Dateien konnten nicht hochgeladen werden',
    failedToClose: 'Ticket konnte nicht geschlossen werden. Bitte versuchen Sie es erneut.',
    failedToSubmitFeedback: 'Feedback konnte nicht gesendet werden',
    cameraPermissionError: 'Kamera kann nicht aufgerufen werden. Überprüfen Sie die Berechtigungen oder versuchen Sie, eine Datei hochzuladen.',
    
    // File Sizes
    bytes: 'Bytes',
    kb: 'KB',
    mb: 'MB',
    gb: 'GB',
    
    // Dashboard - Welcome & Auth
    welcomeBack: 'Willkommen',
    logout: 'Abmelden',
    
    // Dashboard - Sidebar Banners
    accessYourHistory: 'Zugriff auf Ihren Verlauf',
    accessYourHistoryDesc: 'Sehen Sie Ihren Ticket-Verlauf und verwalten Sie Ihr Profil',
    viewTicketHistoryDesc: 'Frühere Tickets anzeigen und Ihre Support-Anfragen verfolgen',
    viewTicketHistoryLink: 'Ticket-Verlauf anzeigen →',
    login: 'Anmelden',
    
    // Dashboard - Survey Banner
    helpUsImprove: 'Helfen Sie uns zu verbessern',
    helpUsImproveDesc: 'Teilen Sie Ihre Erfahrung und helfen Sie uns, Sie besser zu bedienen',
    takeSurvey: 'Umfrage nehmen',
    
    // Dashboard - Quick Tips
    quickTips: 'Schnelle Tipps',
    quickTip1: 'Seien Sie spezifisch über Ihr Problem für eine schnellere Lösung',
    quickTip2: 'Fügen Sie Fehlermeldungen oder Screenshots hinzu, wenn möglich',
    quickTip3: 'Unser KI-Agent ist rund um die Uhr für Sie da',
    
    // Dashboard - Tab Navigation
    supportChat: 'Support-Chat',
    history: 'Verlauf',
    profile: 'Profil',
    
    // Welcome Screen
    welcomeToSupport: 'Willkommen beim NeuroVirtual Support',
    welcomeToSupportDesc: 'Wir sind hier, um Ihnen bei Fragen oder technischen Problemen zu helfen.',
    startChat: 'Chat starten'
  },
  
  fr: {
    // Header
    headerTitle: 'Support NeuroVirtual',
    headerSubtitle: 'Obtenez de l\'aide de notre équipe de support',
    
    // Customer Info Form
    formTitle: 'Commençons',
    nameLabel: 'Votre Nom *',
    namePlaceholder: 'Jean Dupont',
    emailLabel: 'Adresse Email *',
    emailPlaceholder: 'jean@entreprise.fr',
    phoneLabel: 'Numéro de Téléphone',
    phonePlaceholder: '+33-1-55-55-01-23',
    companyLabel: 'Entreprise',
    companyPlaceholder: 'Entreprise SARL',
    countryLabel: 'Pays *',
    countryPlaceholder: 'Sélectionnez votre pays...',
    categoryLabel: 'Comment pouvons-nous vous aider ? *',
    categoryPlaceholder: 'Sélectionnez un sujet...',
    languageLabel: 'Langue Préférée *',
    languagePlaceholder: 'Sélectionnez votre langue...',
    
    // Info Messages
    savedInfoMessage: 'Vos informations sont sauvegardées pour les visites futures',
    clearSavedInfo: 'Effacer les Informations Sauvegardées',
    invalidEmail: 'Veuillez saisir une adresse email valide',
    
    // Ticket Messages
    ticketCreated: 'Ticket Créé',
    conversationStarted: 'Votre conversation a commencé. Notre équipe de support répondra sous peu.',
    closeTicket: 'Fermer le Ticket',
    close: 'Fermer',
    
    // Chat Messages
    startConversation: 'Commencez une conversation avec notre équipe de support !',
    fillDetailsFirst: 'Remplissez vos détails ci-dessus et tapez votre message ci-dessous.',
    typeMessage: 'Tapez votre message...',
    fillDetailsAbove: 'Veuillez d\'abord remplir vos détails ci-dessus',
    you: 'Vous',
    supportAgent: 'Agent de Support',
    initialMessage: 'Bonjour, j\'ai besoin d\'aide avec mon appareil NeuroVirtual.',
    
    // File Upload
    attachFiles: 'Joindre des fichiers',
    takePhoto: 'Prendre une photo',
    uploading: 'Téléchargement',
    download: 'Télécharger',
    
    // Typing Indicator
    agentTyping: 'L\'agent tape...',
    
    // Ticket Status
    conversationClosed: 'Conversation Fermée',
    conversationClosedInactivity: 'Conversation Fermée pour Inactivité',
    ticketClosedByAgent: 'Ticket Fermé par l\'Agent',
    conversationEnded: 'Conversation Terminée',
    sessionTimedOut: 'Session Expirée',
    ticketResolvedByAgent: 'Ticket Résolu par l\'Agent',
    thankYou: 'Merci d\'avoir contacté notre équipe de support.',
    thankYouResolved: 'Votre demande de support a été résolue. Merci d\'avoir contacté notre équipe de support.',
    startNewConversation: 'Commencer une Nouvelle Conversation',
    
    // Close Confirmation Dialog
    closeConversationTitle: 'Fermer la Conversation',
    closeConversationMessage: 'Êtes-vous sûr de vouloir fermer cette conversation ? Cette action ne peut pas être annulée. Si vous avez besoin d\'aide plus tard, vous devrez commencer une nouvelle conversation.',
    cancel: 'Annuler',
    closeConversationButton: 'Fermer la Conversation',
    
    // Resolution Feedback Dialog
    problemResolvedTitle: 'Votre problème a-t-il été résolu ?',
    problemResolvedMessage: 'Avant de fermer cette conversation, dites-nous si votre problème a été résolu. Cela nous aide à améliorer notre support.',
    problemResolved: 'Oui, mon problème a été résolu',
    problemPartiallyResolved: 'Partiellement résolu',
    problemNotResolved: 'Non, mon problème n\'a pas été résolu',
    
    // Abandonment Warning Dialog
    stillThereTitle: 'Êtes-vous toujours là ?',
    stillThereMessage: 'Nous n\'avons pas eu de nouvelles de vous depuis un moment. Pour maintenir cette conversation active et vous assurer de recevoir de l\'aide, faites-nous savoir que vous êtes toujours là.',
    stillThereCountdown: 'Ce chat sera automatiquement fermé dans {seconds} secondes si nous n\'avons pas de nouvelles de vous.',
    yesImHere: 'Oui, je suis là',
    closeChat: 'Fermer le chat',
    
    // Persistent Survey
    surveyTitle: 'Comment nous en sortons-nous ?',
    surveyTitleCompleted: 'Merci pour votre retour !',
    surveyMessage: 'Dites-nous si votre problème a été résolu',
    surveyResolved: 'Oui, résolu',
    surveyPartiallyResolved: 'Partiellement résolu',
    surveyNotResolved: 'Non résolu',
    
    // System Messages
    systemMessage: 'Système',
    conversationClosedDueToInactivity: 'Conversation Fermée pour Inactivité',
    
    // Error Messages
    failedToStart: 'Échec du démarrage de la conversation. Veuillez réessayer.',
    failedToSend: 'Échec de l\'envoi du message. Veuillez réessayer.',
    failedToUpload: 'Échec du téléchargement des fichiers',
    failedToClose: 'Échec de la fermeture du ticket. Veuillez réessayer.',
    failedToSubmitFeedback: 'Échec de l\'envoi du retour',
    cameraPermissionError: 'Impossible d\'accéder à la caméra. Vérifiez les permissions ou essayez de télécharger un fichier.',
    
    // File Sizes
    bytes: 'Octets',
    kb: 'Ko',
    mb: 'Mo',
    gb: 'Go',
    
    // Dashboard - Welcome & Auth
    welcomeBack: 'Bienvenue',
    logout: 'Se déconnecter',
    
    // Dashboard - Sidebar Banners
    accessYourHistory: 'Accédez à votre Historique',
    accessYourHistoryDesc: 'Consultez votre historique de tickets et gérez votre profil',
    viewTicketHistoryDesc: 'Voir les tickets précédents et suivre vos demandes d\'assistance',
    viewTicketHistoryLink: 'Voir l\'Historique des Tickets →',
    login: 'Se connecter',
    
    // Dashboard - Survey Banner
    helpUsImprove: 'Aidez-nous à nous améliorer',
    helpUsImproveDesc: 'Partagez votre expérience et aidez-nous à mieux vous servir',
    takeSurvey: 'Répondre au Sondage',
    
    // Dashboard - Quick Tips
    quickTips: 'Conseils Rapides',
    quickTip1: 'Soyez précis sur votre problème pour une résolution plus rapide',
    quickTip2: 'Incluez des messages d\'erreur ou des captures d\'écran si possible',
    quickTip3: 'Notre agent IA est disponible 24/7 pour vous aider',
    
    // Dashboard - Tab Navigation
    supportChat: 'Chat d\'Assistance',
    history: 'Historique',
    profile: 'Profil',
    
    // Welcome Screen
    welcomeToSupport: 'Bienvenue au Support NeuroVirtual',
    welcomeToSupportDesc: 'Nous sommes là pour vous aider avec vos questions ou problèmes techniques.',
    startChat: 'Démarrer le Chat'
  }
};

interface CustomerTranslationContextType {
  language: string;
  setLanguage: (language: string) => void;
  t: CustomerTranslations;
  formatCountdown: (template: string, seconds: number) => string;
}

const CustomerTranslationContext = createContext<CustomerTranslationContextType | undefined>(undefined);

export const CustomerTranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<string>(() => {
    // Try to get saved language from localStorage first
    try {
      const savedLanguage = localStorage.getItem('customer-language');
      if (savedLanguage && AVAILABLE_LANGUAGES.includes(savedLanguage)) {
        console.log('🔄 Using saved language:', savedLanguage);
        return savedLanguage;
      }
    } catch (error) {
      console.error('Error reading saved language:', error);
    }
    
    // If no saved language, detect from browser
    const detectedLanguage = detectBrowserLanguage();
    console.log('🌍 Auto-detected language:', detectedLanguage);
    return detectedLanguage;
  });

  // Save language preference when it changes
  useEffect(() => {
    try {
      localStorage.setItem('customer-language', language);
      console.log('💾 Language preference saved:', language);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }, [language]);

  const t = translations[language] || translations.en;

  const formatCountdown = (template: string, seconds: number): string => {
    return template.replace('{seconds}', seconds.toString());
  };

  return (
    <CustomerTranslationContext.Provider value={{ language, setLanguage, t, formatCountdown }}>
      {children}
    </CustomerTranslationContext.Provider>
  );
};

export const useCustomerTranslation = (): CustomerTranslationContextType => {
  const context = useContext(CustomerTranslationContext);
  if (context === undefined) {
    throw new Error('useCustomerTranslation must be used within a CustomerTranslationProvider');
  }
  return context;
};

// Export available languages for use in components
export { AVAILABLE_LANGUAGES };

// Export language detection function for testing/debugging
export { detectBrowserLanguage };

export default CustomerTranslationContext; 